import { Routes } from '@angular/router';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { authGuard } from './core/auth/auth.guard';
import { roleGuard } from './core/auth/role.guard';
import { unsavedChangesGuard } from './core/navigation/unsaved-changes.guard';
import { nonProductionGuard } from './core/auth/non-production.guard';

/**
 * Zentrale Routen-Konfiguration des Production Planners.
 *
 * Struktur:
 *  /login              → LoginComponent (kein Guard, kein Layout)
 *  /auth               → MsalRedirectComponent (MSAL-Callback)
 *  /unauthorized       → UnauthorizedComponent (kein RoleGuard)
 *  Alle anderen Routen → MainLayoutComponent (Navbar + Sidebar) + Guards
 *
 * Routen-Schutz via Doppel-Guard:
 *  1. `authGuard`  – prueft ob der User eingeloggt ist (MSAL oder Supabase), leitet sonst zu /login
 *  2. `roleGuard`  – prueft ob der angemeldete Benutzer die erforderliche Rolle besitzt
 *
 * Alle Feature-Module werden per Lazy Loading geladen (loadComponent / loadChildren),
 * um die initiale Bundle-Größe zu minimieren.
 *
 * Routen-Schutz via Berechtigungen:
 *  - `data: { berechtigung: 'Bereich:Lesen' }` – dynamisches Rollenmodell (bevorzugt)
 *  - `data: { roles: ['SystemAdmin', 'TenantAdmin'] }` – nur noch fuer System-Rollen-Routen
 *
 * Rollenmodell (aus CLAUDE.md):
 *  SystemAdmin  – Plattform-Admin (immer erlaubt)
 *  TenantAdmin  – Tenant verwalten, User einladen (immer erlaubt)
 *  Normale User – Zugriff ueber TenantRollen-Berechtigungen (dynamisch)
 */
export const routes: Routes = [

  /**
   * MSAL-Redirect-Handler.
   * Verarbeitet den Rücksprung von Azure AD nach dem Login.
   * Ohne Guards und ohne Layout.
   */
  { path: 'auth', component: MsalRedirectComponent },

  /**
   * Login-Seite.
   * Kein Guard – öffentlich zugänglich.
   * Kein Layout (keine Navbar/Sidebar).
   */
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component')
        .then(m => m.LoginComponent)
  },

  /**
   * Registrierung.
   * Oeffentliche Route – kein Guard, kein Layout.
   */
  {
    path: 'registrieren',
    loadComponent: () =>
      import('./features/auth/registrierung/registrierung.component')
        .then(m => m.RegistrierungComponent)
  },

  /**
   * Registrierungs-Bestaetigung via E-Mail-Link.
   * Oeffentliche Route – kein Guard, kein Layout.
   */
  {
    path: 'registrierung-bestaetigen/:token',
    loadComponent: () =>
      import('./features/auth/registrierung-bestaetigen/registrierung-bestaetigen.component')
        .then(m => m.RegistrierungBestaetigenComponent)
  },



  /**
   * Kein Zugang – User ist eingeloggt, aber keinem Tenant zugeordnet.
   * Zeigt Fehlermeldung mit Support-Kontakt. Kein Guard, kein Layout.
   */
  {
    path: 'kein-zugang',
    loadComponent: () =>
      import('./features/auth/kein-zugang/kein-zugang.component')
        .then(m => m.KeinZugangComponent)
  },

  /**
   * Supabase OAuth Callback.
   * Google/Meta leiten nach dem Login hierher zurueck.
   * Die Komponente verarbeitet den URL-Hash und leitet zum Dashboard weiter.
   */
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/callback/auth-callback.component')
        .then(m => m.AuthCallbackComponent)
  },

  /**
   * Passwort-Neu-Setzen nach Reset-Link aus der E-Mail.
   * Token kommt als URL-Parameter vom eigenen Backend.
   */
  {
    path: 'auth/passwort-neu/:token',
    loadComponent: () =>
      import('./features/auth/passwort-neu/passwort-neu.component')
        .then(m => m.PasswortNeuComponent)
  },

  /**
   * API-Fehlerseite.
   * Wird angezeigt wenn das Backend nicht erreichbar ist (Netzwerkfehler, Server offline).
   * Kein Guard, kein Layout.
   */
  {
    path: 'api-fehler',
    loadComponent: () =>
      import('./features/auth/api-fehler/api-fehler.component')
        .then(m => m.ApiFehlerComponent)
  },

  /**
   * Einladungs-Akzeptierung.
   * Oeffentliche Route – kein authGuard, kein roleGuard, kein MainLayout.
   * Die Komponente startet den Microsoft-Login selbst und ruft anschließend
   * POST /api/einladungen/akzeptieren auf.
   */
  {
    path: 'einladung/:token',
    loadComponent: () =>
      import('./features/auth/einladung-annehmen/einladung-annehmen.component')
        .then(m => m.EinladungAnnehmenComponent)
  },

  /**
   * Kurzlink-Resolver.
   * Oeffentliche Route – kein authGuard, kein roleGuard, kein MainLayout.
   * Leitet den Browser zur Backend-API weiter, die den Kurzlink aufloest
   * und per 302 zur Zielseite im Portal redirectet.
   * Genutzt von Felix (WhatsApp) fuer Entity-Links.
   */
  {
    path: 'l/:code',
    loadComponent: () =>
      import('./features/kurzlink/kurzlink-resolver.component')
        .then(m => m.KurzlinkResolverComponent)
  },

  /**
   * Dev-Login fuer automatisierte Tests.
   * Nur in Non-Production verfuegbar (NonProductionGuard).
   * Verwendung: /dev-login?email=test@example.com
   */
  {
    path: 'dev-login',
    canActivate: [nonProductionGuard],
    loadComponent: () =>
      import('./features/auth/dev-login/dev-login.component')
        .then(m => m.DevLoginComponent)
  },

  /**
   * Unauthorized-Seite.
   * Ziel des RoleGuard wenn der Benutzer keine passende Rolle hat.
   * Ohne MainLayout – klares, ablenkungsfreies Feedback.
   */
  {
    path: 'unauthorized',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/unauthorized/unauthorized.component')
        .then(m => m.UnauthorizedComponent)
  },

  /**
   * Alle geschützten Routen unter MainLayoutComponent.
   * MainLayout stellt Navbar + Sidebar bereit.
   * Jede Kind-Route hat zusaetzlich authGuard + roleGuard.
   */
  {
    path: '',
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component')
        .then(m => m.MainLayoutComponent),
    children: [

      /**
       * Standard-Redirect: Leere URL leitet zum Dashboard.
       */
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

      /**
       * Dashboard (KPIs, Timeline, Unzugeordnete Nachrichten).
       * Fuer alle angemeldeten Rollen sichtbar (Inhalt rollenabhaengig).
       */
      {
        path: 'dashboard',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Dashboard:Lesen' },
        loadComponent: () =>
          import('./features/dashboard/dashboard.component')
            .then(m => m.DashboardComponent)
      },
      {
        path: 'dashboard/widget-katalog',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Dashboard:Lesen' },
        loadComponent: () =>
          import('./features/dashboard/widget-katalog/widget-katalog.component')
            .then(m => m.WidgetKatalogComponent)
      },

      /**
       * Benachrichtigungen – Vollliste (Glocke verlinkt hierher „Alle ansehen“).
       */
      {
        path: 'benachrichtigungen',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Dashboard:Lesen' },
        loadComponent: () =>
          import('./features/benachrichtigungen/benachrichtigungen-page.component')
            .then(m => m.BenachrichtigungenPageComponent)
      },

      /**
       * Redirect-Aliase fuer alte dashboard/-Routen (Bookmarks/History).
       */
      { path: 'dashboard/maengel',              redirectTo: '/maengel',              pathMatch: 'full' },
      { path: 'dashboard/maengel/neu',           redirectTo: '/maengel/neu',           pathMatch: 'full' },
      { path: 'dashboard/nachtraege',            redirectTo: '/nachtraege',            pathMatch: 'full' },
      { path: 'dashboard/nachtraege/neu',        redirectTo: '/nachtraege/neu',        pathMatch: 'full' },
      { path: 'dashboard/behinderungsanzeigen',  redirectTo: '/behinderungsanzeigen',  pathMatch: 'full' },
      { path: 'dashboard/behinderungsanzeigen/neu', redirectTo: '/behinderungsanzeigen/neu', pathMatch: 'full' },
      { path: 'dashboard/bedenkenanzeigen',      redirectTo: '/bedenkenanzeigen',      pathMatch: 'full' },
      { path: 'dashboard/bedenkenanzeigen/neu',  redirectTo: '/bedenkenanzeigen/neu',  pathMatch: 'full' },

      /**
       * Mängel (flache Route).
       * Fuer alle Rollen – Backend filtert per ProjektZugriffService.
       */
      {
        path: 'maengel',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Maengel:Lesen' },
        children: [
          {
            path: '',
            data: { fab: { icon: '+', label: 'Neuer Mangel', route: '/maengel/neu' } },
            loadComponent: () =>
              import('./features/dashboard/maengel/dashboard-maengel.component')
                .then(m => m.DashboardMaengelComponent)
          },
          {
            path: 'neu',
            loadComponent: () =>
              import('./features/dashboard/maengel/mangel-form/mangel-form.component')
                .then(m => m.MangelFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/dashboard/maengel/mangel-form/mangel-form.component')
                .then(m => m.MangelFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },

      /**
       * Behinderungsanzeigen (flache Route).
       * Erkannte Behinderungsanzeigen aus den Aktivitäten.
       */
      {
        path: 'behinderungsanzeigen',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Behinderungsanzeigen:Lesen' },
        children: [
          {
            path: '',
            data: { fab: { icon: '+', label: 'Neue Behinderungsanzeige', route: '/behinderungsanzeigen/neu' } },
            loadComponent: () =>
              import('./features/dashboard/behinderungsanzeigen/dashboard-behinderungsanzeigen.component')
                .then(m => m.DashboardBehinderungsanzeigenComponent)
          },
          {
            path: 'neu',
            loadComponent: () =>
              import('./features/dashboard/behinderungsanzeigen/behinderung-form/behinderung-form.component')
                .then(m => m.BehinderungFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/dashboard/behinderungsanzeigen/behinderung-form/behinderung-form.component')
                .then(m => m.BehinderungFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },

      /**
       * Bedenkenanzeigen (flache Route, § 4 Abs. 3 VOB/B).
       * Bedenken gegen Vorleistungen, Material, Anweisungen etc.
       */
      {
        path: 'bedenkenanzeigen',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Bedenkenanzeigen:Lesen' },
        children: [
          {
            path: '',
            data: { fab: { icon: '+', label: 'Neue Bedenkenanzeige', route: '/bedenkenanzeigen/neu' } },
            loadComponent: () =>
              import('./features/dashboard/bedenkenanzeigen/dashboard-bedenkenanzeigen.component')
                .then(m => m.DashboardBedenkenanzeigenComponent)
          },
          {
            path: 'neu',
            loadComponent: () =>
              import('./features/dashboard/bedenkenanzeigen/bedenken-form/bedenken-form.component')
                .then(m => m.BedenkenFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/dashboard/bedenkenanzeigen/bedenken-form/bedenken-form.component')
                .then(m => m.BedenkenFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },

      /**
       * Nachträge (flache Route).
       * Fuer alle Rollen – Backend filtert per ProjektZugriffService.
       */
      {
        path: 'nachtraege',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Nachtraege:Lesen' },
        children: [
          {
            path: '',
            data: { fab: { icon: '+', label: 'Neuer Nachtrag', route: '/nachtraege/neu' } },
            loadComponent: () =>
              import('./features/dashboard/nachtraege/dashboard-nachtraege.component')
                .then(m => m.DashboardNachtraegeComponent)
          },
          {
            path: 'neu',
            loadComponent: () =>
              import('./features/dashboard/nachtraege/nachtrag-form/nachtrag-form.component')
                .then(m => m.NachtragFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/dashboard/nachtraege/nachtrag-form/nachtrag-form.component')
                .then(m => m.NachtragFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },


      { path: 'dashboard/meldungen', redirectTo: '/aktivitaeten', pathMatch: 'full' },

      /**
       * Meldung erfassen (Vollbild-Formular).
       * Aufgerufen vom Maschinen-Hub mit QueryParam maschineId.
       * Benutzt canDeactivate fuer Unsaved-Changes-Schutz.
       */
      {
        path: 'meldungen/neu',
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { depth: 4 },
        loadComponent: () =>
          import('./features/meldungen/meldung-form/meldung-form.component')
            .then(m => m.MeldungFormComponent)
      },
      {
        path: 'meldungen/:id',
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { depth: 4 },
        loadComponent: () =>
          import('./features/meldungen/meldung-form/meldung-form.component')
            .then(m => m.MeldungFormComponent)
      },

      /**
       * Dashboard-Detail: Mitarbeiter-Aktivitaet.
       * Nur fuer Planer und hoeher.
       */
      {
        path: 'dashboard/aktivitaet',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen' },
        loadComponent: () =>
          import('./features/dashboard/aktivitaet/dashboard-aktivitaet.component')
            .then(m => m.DashboardAktivitaetComponent)
      },


      /**
       * Profilseite: Persoenliche Einstellungen des angemeldeten Users.
       * Fuer alle Rollen – jeder User kann sein eigenes Profil bearbeiten.
       */
      {
        path: 'profil',
        canActivate: [authGuard],
        data: { depth: 1 },
        loadComponent: () =>
          import('./features/profil/profil.component')
            .then(m => m.ProfilComponent)
      },

      /**
       * Aktivitaeten-Uebersicht (Wochen-Timeline aller Ereignisse).
       * Fuer alle angemeldeten Rollen – Backend filtert per ProjektZugriffService.
       */
      {
        path: 'aktivitaeten',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Dashboard:Lesen' },
        loadComponent: () =>
          import('./features/dashboard/aktivitaeten/aktivitaeten-uebersicht.component')
            .then(m => m.AktivitaetenUebersichtComponent)
      },
      {
        path: 'aktivitaeten/:id',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Dashboard:Lesen' },
        loadComponent: () =>
          import('./features/dashboard/aktivitaeten/aktivitaeten-eintrag.component')
            .then(m => m.AktivitaetenEintragComponent)
      },

      /**
       * Projektübersicht + Detail (CRUD, Mitarbeiter, Tagebuch).
       * Zugang ueber Berechtigung 'Projekte:Lesen'.
       */
      {
        path: 'projekte',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Projekte:Lesen' },
        loadChildren: () =>
          import('./features/projekte/projekte.module')
            .then(m => m.ProjekteModule)
      },

      /**
       * Arbeitsbereiche-Hub (Werkstatt, Halle, Buero etc.).
       * Zugang ueber Berechtigung 'Planung:Lesen'.
       */
      {
        path: 'arbeitsbereiche',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen', depth: 2 },
        loadChildren: () =>
          import('./features/arbeitsbereiche/arbeitsbereiche.routes')
            .then(m => m.ARBEITSBEREICHE_ROUTES)
      },

      /**
       * Arbeitsbereich-Hub (Typ-Hub + Bereich-Hub mit Aufgaben, Maschinen, Mitarbeiter, Wartungen).
       * Zugang ueber Feature 'arbeitsbereiche' + Berechtigung 'Arbeitsbereiche:Lesen'.
       */
      {
        path: 'hubs',
        canActivate: [authGuard, roleGuard],
        data: { feature: 'arbeitsbereiche', berechtigung: 'Arbeitsbereiche:Lesen', depth: 1 },
        loadChildren: () =>
          import('./features/hubs/hubs.routes')
            .then(m => m.HUBS_ROUTES)
      },

      /**
       * Arbeitsbereich-Typ-Berichte (BB-19-P4).
       * Stub-Implementierung – wird in BB-19-P4 vollstaendig ausgebaut.
       * Scope: ArbeitsbereichTyp → KPI-Uebersicht, Monatsabschluss etc.
       */
      {
        path: 'arbeitsbereichtypen',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen', depth: 2 },
        loadChildren: () =>
          import('./features/arbeitsbereiche/arbeitsbereiche.routes')
            .then(m => m.ARBEITSBEREICHTYPEN_ROUTES)
      },

      /**
       * Zeitplanung – Dynamische Route fuer alle Bereiche.
       * Der :bereichId Parameter wird aus der TenantConfig aufgeloest.
       */
      {
        path: 'zeitplan/:bereichId',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen' },
        loadComponent: () =>
          import('./features/zeitplan/coming-soon/coming-soon.component')
            .then(m => m.ComingSoonComponent)
      },

      /**
       * Gantt-Übersicht (auftragsübergreifend).
       * Zugang ueber Berechtigung 'Planung:Lesen'.
       */
      {
        path: 'gantt',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen' },
        loadChildren: () =>
          import('./features/gantt/gantt.module')
            .then(m => m.GanttModule)
      },

      /**
       * Planungs-Bereich (Block-Wizard, Abhängigkeiten).
       * Zugang ueber Berechtigung 'Planung:Bearbeiten'.
       */
      {
        path: 'planung',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Bearbeiten' },
        loadChildren: () =>
          import('./features/planung/planung.module')
            .then(m => m.PlanungModule)
      },

      /**
       * Konflikte-Übersicht.
       * Zugang ueber Berechtigung 'Planung:Lesen'.
       */
      {
        path: 'konflikte',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Lesen' },
        loadComponent: () =>
          import('./features/konflikte/konflikte.component')
            .then(m => m.KonflikteComponent)
      },

      /**
       * Aufgaben (An mich / Von mir).
       * Zugang ueber Berechtigung 'EigeneAufgaben:Lesen'.
       */
      {
        path: 'aufgaben',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'EigeneAufgaben:Lesen' },
        loadChildren: () =>
          import('./features/aufgaben/aufgaben.module')
            .then(m => m.AufgabenModule)
      },

      /**
       * Aufgaben-Serien (Wiederkehrende Routinen).
       * Zugang ueber Berechtigung 'EigeneAufgaben:Bearbeiten'.
       */
      {
        path: 'aufgaben-serien',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'EigeneAufgaben:Bearbeiten' },
        loadChildren: () =>
          import('./features/aufgaben-serien/aufgaben-serien.module')
            .then(m => m.AufgabenSerienModule)
      },

      /**
       * VorlaufAufgaben (AV / Vorlauf).
       * AV-Pakete und Beschaffungsaufgaben – Berechtigung 'Planung:Bearbeiten'.
       */
      {
        path: 'vorlauf',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Planung:Bearbeiten' },
        loadChildren: () =>
          import('./features/vorlauf/vorlauf.module')
            .then(m => m.VorlaufModule)
      },

      /**
       * Meine Zeiten (Zeiterfassung).
       * Zugang ueber Berechtigung 'Zeiterfassung:Lesen'.
       */
      {
        path: 'meine-zeiten',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Zeiterfassung:Lesen', depth: 2 },
        loadChildren: () =>
          import('./features/meine-zeit/meine-zeit.routes')
            .then(m => m.MEINE_ZEIT_ROUTES)
      },

      /**
       * Todos – aggregierte persoenliche Aktions-Zentrale (ehemals /inbox).
       * Zeigt Aufgaben, Nachtraege, Maengel, Behinderungen, Bedenken gefiltert auf den eingeloggten User.
       */
      {
        path: 'todos',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'EigeneAufgaben:Lesen' },
        loadChildren: () =>
          import('./features/inbox/inbox.module')
            .then(m => m.InboxModule)
      },

      /**
       * Legacy-Redirect: /inbox → /todos (Rueckwaertskompatibilitaet).
       */
      {
        path: 'inbox',
        redirectTo: '/todos',
        pathMatch: 'full'
      },

      /**
       * Legacy-Route – leitet auf /todos weiter.
       */
      {
        path: 'meine-aufgaben',
        redirectTo: '/todos',
        pathMatch: 'full'
      },


      /**
       * Lieferantenverwaltung (Liste, Anlegen, Bearbeiten).
       * Zugang ueber Berechtigung 'Stammdaten:Lesen'.
       */
      {
        path: 'lieferanten',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Stammdaten:Lesen' },
        loadChildren: () =>
          import('./features/lieferanten/lieferanten.module')
            .then(m => m.LieferantenModule)
      },

      /**
       * Kontaktbuch (Liste, Anlegen, Bearbeiten).
       * Globales tenant-isoliertes Kontaktbuch fuer externe Personen und Firmen.
       * Zugang ueber Berechtigung 'Kontakte:Lesen'.
       */
      {
        path: 'kontakte',
        canActivate: [authGuard, roleGuard],
        data: { berechtigung: 'Kontakte:Lesen' },
        loadChildren: () =>
          import('./features/kontakte/kontakte.module')
            .then(m => m.KontakteModule)
      },

      /**
       * Felix-Sammel-Mailbox: Liste und Nachricht bearbeiten (eigene Route wie Kontakte).
       */
      {
        path: 'felix-posteingang/:id',
        canActivate: [authGuard, roleGuard],
        data: {
          berechtigung: 'FelixPosteingang:Lesen',
          depth: 3,
        },
        loadComponent: () =>
          import('./features/felix-posteingang/felix-posteingang-detail.component')
            .then(m => m.FelixPosteingangDetailComponent),
      },
      {
        path: 'felix-posteingang',
        canActivate: [authGuard, roleGuard],
        data: {
          berechtigung: 'FelixPosteingang:Lesen',
          depth: 2,
        },
        loadComponent: () =>
          import('./features/felix-posteingang/felix-posteingang.component')
            .then(m => m.FelixPosteingangComponent),
      },

      /**
       * Kalender (Termine + Mail-Integration).
       * Lazy-loaded Modul mit Liste, Termin-Form, Einstellungen.
       */
      {
        path: 'kalender',
        canActivate: [authGuard],
        data: { depth: 2 },
        loadChildren: () =>
          import('./features/kalender/kalender.module')
            .then(m => m.KalenderModule)
      },

      /**
       * Stammdaten (Mitarbeiter-Funktionen etc.).
       * Nur TenantAdmin und SystemAdmin haben Zugriff.
       */
      {
        path: 'stammdaten',
        canActivate: [authGuard, roleGuard],
        data: { roles: ['SystemAdmin', 'TenantAdmin'] },
        loadChildren: () =>
          import('./features/stammdaten/stammdaten.module')
            .then(m => m.StammdatenModule)
      },

      /**
       * Admin-Bereich (User einladen, Rollen verwalten, Tenant-Konfiguration).
       * Nur TenantAdmin und SystemAdmin haben Zugriff.
       */
      {
        path: 'admin',
        canActivate: [authGuard, roleGuard],
        data: { roles: ['SystemAdmin', 'TenantAdmin'] },
        loadChildren: () =>
          import('./features/admin/admin.module')
            .then(m => m.AdminModule)
      },

      /**
       * System-Bereich (Tenant-Verwaltung, Plattform-Konfiguration).
       * Nur SystemAdmin hat Zugriff.
       */
      {
        path: 'system',
        canActivate: [authGuard, roleGuard],
        data: { roles: ['SystemAdmin'] },
        loadChildren: () =>
          import('./features/system/system.module')
            .then(m => m.SystemModule)
      },

      /**
       * Mail-Versand-Seite (Vollseite statt Dialog).
       * Wird via Router-Navigation mit Query-Parametern aufgerufen.
       * Fuer alle angemeldeten Rollen zugaenglich.
       */
      {
        path: 'mail-versand',
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { depth: 3 },
        loadComponent: () =>
          import('./shared/components/mail-versand/mail-versand.component')
            .then(m => m.MailVersandComponent)
      },

      /**
       * Felix Chat-Seite (Mobile).
       * Auf Mobile wird Felix als normale Seite gerendert statt als Overlay.
       * Fuer alle angemeldeten Rollen zugaenglich.
       */
      {
        path: 'felix',
        canActivate: [authGuard],
        data: { depth: 1 },
        loadComponent: () =>
          import('./features/felix/felix-seite/felix-seite.component')
            .then(m => m.FelixSeiteComponent)
      },

      /**
       * Wildcard innerhalb des Layouts: unbekannte URLs zum Dashboard.
       * Dashboard ist die Startseite für eingeloggte User; Projektliste wäre ohne
       * Projektauswahl kein sinnvolles Ziel.
       */
      { path: '**', redirectTo: 'dashboard' }
    ]
  }
];
